/**
 * No-show guarantee query + charge/refund mutation layer (card-fingerprint-no-show.md). Keys nest under
 * `["venue", venueId, "guarantees", …]` so the shell's realtime invalidation reaches them; a charge/
 * refund also invalidates the bookings family (the booking's `paymentStatus` changed). The manager
 * step-up mint mirrors `bookings/mutations.ts` with the reserved `NO_SHOW_CHARGE` action (ST-4).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GuaranteeResponse } from "@repo/api-client";
import { requestStepUp } from "../../shell/pinSwitch";
import { noShowChargeApi } from "./noShowChargeApi";

export const noShowChargeKeys = {
  guarantee: (venueId: string, bookingId: string) =>
    ["venue", venueId, "guarantees", bookingId] as const,
  failed: (venueId: string) => ["venue", venueId, "guarantees", "failed"] as const,
};

export function useGuarantee(venueId: string, bookingId: string, enabled = true) {
  return useQuery<GuaranteeResponse>({
    queryKey: noShowChargeKeys.guarantee(venueId, bookingId),
    queryFn: ({ signal }) => noShowChargeApi.get(venueId, bookingId, signal),
    enabled: enabled && bookingId !== "",
    staleTime: 10_000,
  });
}

export function useFailedGuarantees(venueId: string, enabled = true) {
  return useQuery<GuaranteeResponse[]>({
    queryKey: noShowChargeKeys.failed(venueId),
    queryFn: ({ signal }) => noShowChargeApi.failed(venueId, signal),
    enabled,
    staleTime: 30_000,
  });
}

function useInvalidateGuarantees(venueId: string): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["venue", venueId, "guarantees"] }),
      qc.invalidateQueries({ queryKey: ["venue", venueId, "bookings"] }),
    ]);
  };
}

export interface ChargeVars {
  bookingId: string;
  stepUpAuthId: string;
}

export function useChargeNoShow(venueId: string) {
  const invalidate = useInvalidateGuarantees(venueId);
  return useMutation<GuaranteeResponse, unknown, ChargeVars>({
    mutationFn: ({ bookingId, stepUpAuthId }) =>
      noShowChargeApi.charge(venueId, bookingId, stepUpAuthId),
    onSuccess: invalidate,
  });
}

export interface RefundVars {
  bookingId: string;
  reason: string;
}

export function useRefundNoShow(venueId: string) {
  const invalidate = useInvalidateGuarantees(venueId);
  return useMutation<GuaranteeResponse, unknown, RefundVars>({
    mutationFn: ({ bookingId, reason }) => noShowChargeApi.refund(venueId, bookingId, reason),
    onSuccess: invalidate,
  });
}

/**
 * Mint a single-use `NO_SHOW_CHARGE` authorization from a manager PIN (ST-4, the SensitiveAction the
 * roles-and-permissions task reserved for this). The returned `authorizationId` rides as the step-up
 * header on the immediately-following charge. An invalid PIN / lockout surfaces as an `ApiError`.
 */
export function mintNoShowChargeStepUp(
  venueId: string,
  pin: string,
  bookingId: string,
): Promise<{ authorizationId: string; expiresAt: string }> {
  return requestStepUp(venueId, { pin, action: "NO_SHOW_CHARGE", targetRef: bookingId });
}
